import {
  AbstractControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

const emailPatternValidator =
  /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

/**
 * @description
 * Provides a set of custom validators that can be used by form controls.
 */
export class CustomValidators {
  private static CheckUserName(
    keyName: string,
    nameControl: AbstractControl,
    officialNameControl: AbstractControl
  ): ValidationErrors {
    if (/ {2}/.test(nameControl.value)) {
      nameControl.setValue(nameControl.value.toString().replace('  ', ' '));
    }

    let nameTab = CustomValidators.splitName(officialNameControl.value);
    let choosenNameTab = CustomValidators.splitName(nameControl.value);

    if (nameTab.length === 0) {
      nameTab = [officialNameControl.value];
    }

    if (choosenNameTab.length === 0) {
      choosenNameTab = [nameControl.value];
    }

    // Convertir en minuscules
    for (let i = 0; i < choosenNameTab.length; i++) {
      choosenNameTab[i] = choosenNameTab[i]?.toLowerCase();
    }

    for (let i = 0; i < nameTab.length; i++) {
      nameTab[i] = nameTab[i]?.toLowerCase();
    }

    // Vérification des doublons dans le prénom saisi
    const hasDuplicates = choosenNameTab.some((item, index) => {
      return choosenNameTab.indexOf(item) !== index;
    });

    if (hasDuplicates) {
      return {
        [`${keyName}HasDuplicates`]: true,
      };
    }

    // Vérification que chaque élément du prénom saisi se trouve dans le prénom officiel
    if (choosenNameTab.every(elem => nameTab.indexOf(elem) > -1)) {
      return;
    } else {
      return {
        [`${keyName}NotMatch`]: true,
      };
    }
  }

  static CheckUserFirstName(): ValidatorFn {
    return (formGroup: FormGroup) => {
      const officialFirstNameControl = formGroup.get('officialFirstName');
      const firstNameControl = formGroup.get('firstName');

      return this.CheckUserName(
        'firstName',
        firstNameControl,
        officialFirstNameControl
      );
    };
  }

  static CheckUserLastName(): ValidatorFn {
    return (formGroup: FormGroup) => {
      const officialLastNameControl = formGroup.get('officialLastName');
      const lastNameControl = formGroup.get('lastName');

      return this.CheckUserName(
        'lastName',
        lastNameControl,
        officialLastNameControl
      );
    };
  }

  /**
   * @description
   * Validator that requiers the control have a valid email address.
   *
   * Uses a more permissive pattern than Validators.email.
   *
   * @returns An error map with the `pattern` property
   * if the validation check fails, otherwise null.
   *
   * @see {@link Validators.email()}
   */
  static validEmail(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !control) {
        return null;
      }
      return !emailPatternValidator.test(control.value)
        ? { pattern: true }
        : null;
    };
  }

  /**
   * @description
   * Validator that requires the control have an email address not ending by one of the following:
   * - '@edu.vs.ch'
   * - 'educanet2.ch'
   * - 'eptm.ch'
   * - 'epcasion.ch'
   * - 'berufsbildung-vs.ch'
   * - 'epasc.ch'
   *
   * @returns An error map with the `officialemail` property
   * if the validation check fails, otherwise null.
   */
  static EmailInterdiction(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent || !control) {
        return null;
      }
      const email = control.parent.get('email');

      if (email.value) {
        if (
          email.value.includes('@edu.vs.ch') ||
          email.value.includes('educanet2.ch') ||
          email.value.includes('eptm.ch') ||
          email.value.includes('epcasion.ch') ||
          email.value.includes('berufsbildung-vs.ch') ||
          email.value.includes('epasc.ch')
        ) {
          return {
            officialemail: true,
          };
        }
      }
    };
  }

  /**
   * @description
   * Validator that requires the control have an email address ending by one of the following:
   * - '@edu.vs.ch'
   * - 'educanet2.ch'
   * - 'eptm.ch'
   * - 'epcasion.ch'
   * - 'berufsbildung-vs.ch'
   * - 'epasc.ch'
   *
   * @returns An error map with the `contactemail` property
   * if the validation check fails, otherwise null.
   */
  static eduidEmail(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.value || !control) {
        return null;
      }

      if (
        !control.value.includes('@edu.vs.ch') &&
        !control.value.includes('educanet2.ch') &&
        !control.value.includes('eptm.ch') &&
        !control.value.includes('epcasion.ch') &&
        !control.value.includes('berufsbildung-vs.ch') &&
        !control.value.includes('epasc.ch')
      ) {
        return {
          contactemail: true,
        };
      }
    };
  }

  /**
   * @description
   * Validator that requires the control have a password value not matching the oldpassword value.
   *
   * The form must have a `password` and a `oldpassword` controls.
   *
   * @returns An error map with the `oldpasswordmatch` property
   * if the validation check fails, otherwise null.
   */
  static passwordInterdiction(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control.parent || !control) {
        return null;
      }

      const password = control.parent.get('password');
      const oldpassword = control.parent.get('oldpassword');

      if (!password || !oldpassword) {
        return null;
      }

      if (oldpassword.value === '') {
        return null;
      }

      if (password.value == oldpassword.value) {
        return {
          oldpasswordmatch: true,
        };
      }
    };
  }

  /**
   * @description
   * Validator that requires the control have a password value matching the passwordConfirmation value.
   *
   * The form must have a `password` and a `passwordConfirmation` controls.
   *
   * @returns An error map with the `passwordNotMatch` property
   * if the validation check fails, otherwise null.
   */
  static confirmPasswords(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!control) {
        return null;
      }

      const password = control.get('password');
      const passwordConfirm = control.get('passwordConfirmation');

      if (!password || !passwordConfirm) {
        return null;
      }

      if (passwordConfirm.value === '') {
        return null;
      }

      if (password.value === passwordConfirm.value) {
        return null;
      } else {
        return { passwordNotMatch: true };
      }
    };
  }

  /**
   * @description
   * Validator that requires the control have a password complex enough.
   *
   * The password must pass at least three of these checks:
   * - At least one lowercase char;
   * - At least one uppercase char;
   * - At least one number char;
   * - At least one special char.
   *
   * @returns An error map with the `passwordRestriction` property
   * if the validation check fails, otherwise null.
   */
  static passwordRestriction(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let controlCheck = 0;

      const regexMinuscule = new RegExp('(?=.*[a-z])');
      if (regexMinuscule.test(control.value)) {
        controlCheck++;
      }
      const regexMaj = new RegExp('(?=.*[A-Z])');
      if (regexMaj.test(control.value)) {
        controlCheck++;
      }

      const regexNumber = new RegExp('(?=.*[0-9])');
      if (regexNumber.test(control.value)) {
        controlCheck++;
      }

      // Javascript flavored copy of the Midpoint password policy.
      // Allows: !?@#\$%^&()=+{}[]|*<>':;".,/~
      const regexSpecial = /[!?@#\\$%\\^&;()=+{}[\]|\-_*<>':;".,~/]/;
      if (regexSpecial.test(control.value)) {
        controlCheck++;
      }
      if (controlCheck >= 3) {
        return null;
      } else {
        return {
          passwordRestriction: true,
        };
      }
    };
  }

  /**
   * @description
   * Validator that requires the control
   *
   * @param officialFirstName
   * @param officialLastName
   * @returns An error map with the `passwordContainName` property
   * if the validation check fails, otherwise null.
   */
  static passwordNameValidator(
    officialFirstName: string,
    officialLastName: string
  ): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let firstNameTab = this.splitName(officialFirstName);
      let lastNameTab = this.splitName(officialLastName);

      if (firstNameTab.length === 0) {
        firstNameTab = [officialFirstName];
      }
      if (lastNameTab.length === 0) {
        lastNameTab = [officialLastName];
      }

      if (firstNameTab.length === 1 && firstNameTab[0] === undefined) {
        firstNameTab = [];
      }
      if (lastNameTab.length === 1 && lastNameTab[0] === undefined) {
        lastNameTab = [];
      }

      if (firstNameTab.length > 0)
        firstNameTab = firstNameTab.filter(firstname => firstname.length > 2);
      if (lastNameTab.length > 0)
        lastNameTab = lastNameTab.filter(lastname => lastname.length > 2);

      let nameTab = [...firstNameTab, ...lastNameTab];

      // At firt initialization nameTab is undefined for a miliseconds
      if (nameTab[0] !== undefined || nameTab[1] !== undefined) {
        nameTab = nameTab.filter(element => element.length >= 0);
      }

      const regex = new RegExp(nameTab.join('|'), 'i');
      if (regex.test(control.value)) {
        return {
          passwordContainName: true,
        };
      } else {
        return null;
      }
    };
  }

  /**
   * @description
   * Validator that requires the control
   *
   * @param samAccountName
   * @returns
   */
  static passwordSameAccountNameValidator(samAccountName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value) {
        if (control.value.toLowerCase().includes(samAccountName)) {
          return { passwordContainSamAccountName: true };
        }
      } else {
        return null;
      }
    };
  }

  static splitName(name: string): string[] {
    let nameTab: string[];
    if (/\s/.test(name)) {
      if (/-/.test(name)) {
        nameTab = name.split(' ').join('-').split('-');
        nameTab.push('-'); // Ajouter le séparateur à la fin du tableau
        nameTab.push(' '); // Ajouter le séparateur à la fin du tableau
      } else {
        nameTab = name.split(' ');
        nameTab.push(' '); // Ajouter le séparateur à la fin du tableau
      }
    } else {
      if (/-/.test(name)) {
        nameTab = name.split('-');
        nameTab.push('-'); // Ajouter le séparateur à la fin du tableau
      } else {
        nameTab = [];
      }
    }
    return nameTab;
  }

  /**
   * @description
   * Validator that removes the first 0 of a phone number.
   *
   * @returns The validation never fails, always null.
   */
  static phoneValidation(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.value.startsWith('0')) {
        control.setValue(control.value.toString().replace('0', ''));
      }
      return null;
    };
  }

  static capitalizeFirstLetter(value: string): string {
    return value
      .toLowerCase()
      .split(' ')
      .map(s => s.charAt(0).toUpperCase() + s.substring(1))
      .join(' ');
  }

  /**
   *
   * @param name
   * @returns
   */
  static nameValidator(name: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (/ {2}/.test(control.value)) {
        control.setValue(control.value.toString().replace('  ', ' '));
      }

      let nameTab = this.splitName(name);
      let choosenNameTab = this.splitName(control.value);

      if (nameTab.length === 0) {
        nameTab = [name];
      }

      if (choosenNameTab.length === 0) {
        choosenNameTab = [control.value];
      }

      for (let i = 0; i < choosenNameTab.length; i++) {
        choosenNameTab[i] = choosenNameTab[i].toLowerCase();
      }

      for (let i = 0; i < nameTab.length; i++) {
        nameTab[i] = nameTab[i].toLowerCase();
      }

      if (choosenNameTab.every(elem => nameTab.indexOf(elem) > -1)) {
        return null;
      } else {
        return {
          nameNotMatch: true,
        };
      }
    };
  }
}
